import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../features/auth/hooks/useAuthStore'
import { navigationConfig, type NavigationItem } from '../config/navigationConfig'
import { ChevronDown, School2 } from 'lucide-react'
import { useOrganizationProfile } from '../shared/hooks/useOrganizationProfile'
import { getStoredRoleHomePath, normalizeStoredRole } from '../routes/auth'

export type FeesFinanceNavKey =
  | 'overview'
  | 'fee-head'
  | 'fee-structure'
  | 'reports'
  | 'payments'
  | 'discounts'
  | 'approvals'
  | null

export function getFeesFinanceActiveKey(location: { pathname: string; search?: string }): FeesFinanceNavKey {
  const current = location.pathname || ''
  const search = location.search || ''

  const isFeesFinance =
    current.startsWith('/admin/finance/fees-finance') ||
    current.startsWith('/admin/fees-finance') ||
    current.startsWith('/admin/finance/fee-head') ||
    current.startsWith('/admin/fees-finance/fee-head') ||
    current.startsWith('/admin/finance/fee-structure') ||
    current.startsWith('/admin/fees-finance/fee-structure')

  if (!isFeesFinance) return null

  // 1. Explicit path sub-routes (takes precedence over query parameter)
  if (
    current.startsWith('/admin/finance/fee-head') ||
    current.startsWith('/admin/fees-finance/fee-head') ||
    current === '/admin/finance/fees-finance/fee-head' ||
    current.startsWith('/admin/finance/fees-finance/fee-head/') ||
    current === '/admin/fees-finance/fee-head' ||
    current.startsWith('/admin/fees-finance/fee-head/')
  ) {
    return 'fee-head'
  }

  if (
    current.startsWith('/admin/finance/fee-structure') ||
    current.startsWith('/admin/fees-finance/fee-structure') ||
    current === '/admin/finance/fees-finance/fee-structure' ||
    current.startsWith('/admin/finance/fees-finance/fee-structure/') ||
    current === '/admin/fees-finance/fee-structure' ||
    current.startsWith('/admin/fees-finance/fee-structure/')
  ) {
    return 'fee-structure'
  }

  if (
    current === '/admin/finance/fees-finance/reports' ||
    current.startsWith('/admin/finance/fees-finance/reports/') ||
    current === '/admin/fees-finance/reports' ||
    current.startsWith('/admin/fees-finance/reports/')
  ) {
    return 'reports'
  }

  if (
    current === '/admin/finance/fees-finance/payments' ||
    current.startsWith('/admin/finance/fees-finance/payments/') ||
    current === '/admin/fees-finance/payments' ||
    current.startsWith('/admin/fees-finance/payments/')
  ) {
    return 'payments'
  }

  if (
    current === '/admin/finance/fees-finance/discounts' ||
    current.startsWith('/admin/finance/fees-finance/discounts/') ||
    current === '/admin/fees-finance/discounts' ||
    current.startsWith('/admin/fees-finance/discounts/')
  ) {
    return 'discounts'
  }

  if (
    current === '/admin/finance/fees-finance/approvals' ||
    current.startsWith('/admin/finance/fees-finance/approvals/') ||
    current === '/admin/fees-finance/approvals' ||
    current.startsWith('/admin/fees-finance/approvals/')
  ) {
    return 'approvals'
  }

  if (
    current === '/admin/finance/fees-finance/overview' ||
    current.startsWith('/admin/finance/fees-finance/overview/') ||
    current === '/admin/fees-finance/overview' ||
    current.startsWith('/admin/fees-finance/overview/')
  ) {
    return 'overview'
  }

  // 2. Base path with query parameter or default overview tab
  const isBase =
    current === '/admin/finance/fees-finance' ||
    current === '/admin/finance/fees-finance/' ||
    current === '/admin/fees-finance' ||
    current === '/admin/fees-finance/'

  if (isBase) {
    const params = new URLSearchParams(search)
    const tab = params.get('tab')
    if (tab === 'fee-head') return 'fee-head'
    if (tab === 'fee-structure') return 'fee-structure'
    if (tab === 'reports') return 'reports'
    if (tab === 'payments') return 'payments'
    if (tab === 'discounts') return 'discounts'
    if (tab === 'approvals') return 'approvals'
    if (tab === 'overview' || !tab) return 'overview'
  }

  return null
}

interface PermissionAwareSidebarProps {
  open: boolean
  onClose: () => void
}

export function PermissionAwareSidebar({ open, onClose }: PermissionAwareSidebarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission, role, user } = useAuthStore()
  const [openParents, setOpenParents] = useState<Record<string, boolean>>({})

  const { data: orgProfile } = useOrganizationProfile()
  const [imgError, setImgError] = useState(false)

  const schoolName =
    orgProfile?.name ||
    orgProfile?.schoolName ||
    orgProfile?.organizationName ||
    orgProfile?.orgName ||
    orgProfile?.title ||
    'Global Academy'

  const logoUrl =
    orgProfile?.logoUrl ||
    orgProfile?.logo ||
    orgProfile?.logo_url ||
    orgProfile?.shortLivedLogoUrl ||
    null

  useEffect(() => {
    setImgError(false)
  }, [logoUrl])

  // Resolve target path dynamically for general paths like '/dashboard' or '/communication'
  const resolveItemPath = (item: NavigationItem): string => {
    const normalizedRole = normalizeStoredRole(role)
    if (item.path === '/dashboard') {
      if (normalizedRole === 'teacher') {
        return '/teacher/dashboard'
      }
      if (normalizedRole === 'admin' || hasPermission('*')) {
        return '/admin/dashboard'
      }
      return getStoredRoleHomePath()
    }
    if (item.path === '/communication') {
      if (normalizedRole === 'teacher') {
        return '/teacher/communication'
      }
      if (normalizedRole === 'admin' || hasPermission('*')) {
        return '/admin/communication'
      }
    }
    return item.path
  }

  // Filter sections and items strictly based on user permissions
  const filteredSections = navigationConfig
    .map((section) => {
      const allowedItems = section.items.filter((item) => {
        const isAdminOrBranchAdmin =
          role === 'admin' ||
          role === 'superadmin' ||
          role === 'branch_admin' ||
          role === 'branchadmin' ||
          role === 'organizational_admin' ||
          role === 'org_admin' ||
          hasPermission('*')

        if (isAdminOrBranchAdmin) {
          return true
        }

        if (item.path === '/admin/enrollment/seat-management' || item.path === '/admin/class-management/classes') {
          return isAdminOrBranchAdmin
        }

        if (item.path === '/admin/academics' || item.permission === 'academics:view' || item.permission === 'academics.view') {
          return hasPermission('academics.view') || hasPermission('academics:view')
        }

        if (item.path === '/teacher/myclasses' || item.label === 'My Classes') {
          return (
            hasPermission('class-management:view') ||
            hasPermission('class-management.view') ||
            hasPermission('sections:view') ||
            hasPermission('sections.view') ||
            hasPermission('grades:view') ||
            hasPermission('grades.view') ||
            hasPermission('students:view') ||
            hasPermission('students.view') ||
            role === 'STAFF' ||
            role === 'staff' ||
            role === 'teacher' ||
            role === 'TEACHER'
          )
        }

        return hasPermission(item.permission)
      })
      return {
        ...section,
        items: allowedItems,
      }
    })
    .filter((section) => section.items.length > 0)

  const toggleParent = (label: string) => {
    setOpenParents((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  const handleNavClick = (item: NavigationItem) => {
    const targetPath = resolveItemPath(item)
    navigate(targetPath)
    onClose()
  }

  const isPathActive = (targetPath: string): boolean => {
    if (!targetPath) return false
    const current = location.pathname

    if (current === targetPath) return true

    // 0. Academics overview rule: do not activate Academics when on Subject Management
    if (targetPath === '/admin/academics') {
      if (current.startsWith('/admin/academics/subject-management')) {
        return false
      }
      if (current.startsWith('/admin/academics/overview')) {
        return true
      }
      return false
    }

    // 9. User & Role Management Sub-routes (must be evaluated BEFORE generic startsWith)
    if (targetPath === '/admin/user-management') {
      if (
        current === '/admin/user-management' ||
        current === '/admin/user-management/' ||
        current.startsWith('/admin/user-management/users') ||
        current.startsWith('/admin/user-management/create-user') ||
        current.startsWith('/admin/user-management/overview') ||
        current.startsWith('/admin/user-management/user-details')
      ) {
        return true
      }
      return false
    }

    if (targetPath === '/admin/user-management/role-management') {
      if (
        current === '/admin/user-management/role-management' ||
        current.startsWith('/admin/user-management/role-management') ||
        current.startsWith('/admin/user-management/create-role')
      ) {
        return true
      }
    }

    if (targetPath === '/admin/user-management/departments') {
      if (current.startsWith('/admin/user-management/departments')) {
        return true
      }
    }

    if (targetPath === '/admin/user-management/designations') {
      if (current.startsWith('/admin/user-management/designations')) {
        return true
      }
    }

    if (current.startsWith(targetPath + '/')) return true

    // 1. Communication Sub-routes
    if (targetPath === '/communication/message') {
      if (
        current.startsWith('/communication/compose') ||
        current.startsWith('/teacher/communication/compose') ||
        current.startsWith('/communication/message')
      ) {
        return true
      }
    }

    // 2. Student Directory Sub-routes
    if (targetPath === '/admin/student-directory') {
      if (
        current.startsWith('/admin/student-directory') ||
        current.startsWith('/admin/add-student') ||
        current.startsWith('/admin/student-profile') ||
        current.startsWith('/student/')
      ) {
        return true
      }
    }

    // 3. Teacher/Staff Directory Sub-routes
    if (targetPath === '/admin/frontoffice/staff-directory') {
      if (
        current.startsWith('/admin/frontoffice/staff') ||
        current.startsWith('/admin/staff-profile') ||
        current.startsWith('/admin/staff-directory')
      ) {
        return true
      }
    }

    // 4. Seat Management & Admissions Sub-routes
    if (targetPath === '/admin/enrollment/seat-management') {
      if (
        current.startsWith('/admin/enrollment') ||
        current.startsWith('/admin/admissions')
      ) {
        return true
      }
    }

    // 5. Timetable Sub-routes
    if (targetPath === '/academics/timetable') {
      if (
        current.startsWith('/academics/timetable') ||
        current.startsWith('/admin/academics/timetable') ||
        current.startsWith('/teacher/academics/timetable')
      ) {
        return true
      }
    }

    if (targetPath === '/admin/events-calendar') {
      if (
        current.startsWith('/admin/events-calendar') ||
        current.startsWith('/admin/calendar') ||
        current.startsWith('/admin/events/calendar')
      ) {
        return true
      }
    }

    // 7. Class Management Sub-routes
    if (targetPath === '/admin/class-management/classes') {
      if (
        current.startsWith('/admin/class-management/classes') ||
        current.startsWith('/admin/class-management/assign-students') ||
        current.startsWith('/admin/class-management/transfer-students') ||
        current.startsWith('/admin/class-management/promote-students')
      ) {
        return true
      }
    }

    // 8. Lesson Plans Sub-routes
    if (targetPath === '/teacher/lessonplans') {
      if (
        current.startsWith('/teacher/lessonplans') ||
        current.startsWith('/teacher/create-lesson-plan')
      ) {
        return true
      }
    }

    return false
  }

  const isChildActive = (parentItem: NavigationItem, childItem: NavigationItem): boolean => {
    const isFeesFinanceParent = parentItem.label === 'Fees & Finance' || parentItem.path.includes('fees-finance')
    if (isFeesFinanceParent) {
      const activeKey = getFeesFinanceActiveKey(location)
      if (!activeKey) return false
      const p = childItem.path.toLowerCase()
      if (p.endsWith('/overview') || p.includes('tab=overview')) return activeKey === 'overview'
      if (p.endsWith('/fee-head') || p.includes('tab=fee-head')) return activeKey === 'fee-head'
      if (p.endsWith('/fee-structure') || p.includes('tab=fee-structure')) return activeKey === 'fee-structure'
      if (p.endsWith('/payments') || p.includes('tab=payments')) return activeKey === 'payments'
      if (p.endsWith('/discounts') || p.includes('tab=discounts')) return activeKey === 'discounts'
      if (p.endsWith('/reports') || p.includes('tab=reports')) return activeKey === 'reports'
      if (p.endsWith('/approvals') || p.includes('tab=approvals')) return activeKey === 'approvals'
      return false
    }
    return isPathActive(childItem.path)
  }

  const isParentActive = (item: NavigationItem): boolean => {
    const targetPath = resolveItemPath(item)
    if (item.exact) {
      return location.pathname === targetPath
    }
    return isPathActive(targetPath)
  }

  const isParentExpanded = (item: NavigationItem): boolean => {
    if (!item.children || item.children.length === 0) return false
    if (openParents[item.label] !== undefined) {
      return openParents[item.label]
    }
    return item.children.some((child) => isChildActive(item, child))
  }

  const portalSublabel =
    role === 'admin'
      ? 'School Administrator'
      : role === 'teacher'
        ? 'Teacher Portal'
        : `${user?.name || 'User'} Portal`

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex w-[240px] flex-col overflow-y-auto bg-[#005156] px-4 pb-5 pt-4 text-white font-['Inter',sans-serif] transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .sidebar-scroll-custom {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            .sidebar-scroll-custom::-webkit-scrollbar {
              width: 0;
              height: 0;
            }
          `,
        }}
      />

      {/* Brand & Portal Header */}
      <div
        className="flex cursor-pointer items-center gap-3 rounded-3xl px-3 py-2"
        onClick={() => {
          navigate(role === 'teacher' ? '/teacher/dashboard' : '/admin/dashboard')
          onClose()
        }}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/18 bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden">
          {logoUrl && !imgError ? (
            <img
              src={logoUrl}
              alt={schoolName}
              onError={() => setImgError(true)}
              className="h-full w-full object-contain p-1"
            />
          ) : (
            <School2 className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-[17px] font-bold leading-none text-white tracking-tight truncate" title={schoolName}>
            {schoolName}
          </h1>
          <p className="mt-1.5 text-[11px] font-medium text-white/65 leading-none">
            {portalSublabel}
          </p>
        </div>
      </div>

      {/* Navigation Sections */}
      <nav className="sidebar-scroll-custom mt-6 flex flex-1 flex-col pr-1">
        {filteredSections.map((section) => (
          <div key={section.title} className="mb-4">
            <p className="px-3 text-[11px] font-bold uppercase tracking-[1.1px] text-white/40 mt-3 mb-2">
              {section.title}
            </p>

            <div className="flex flex-col gap-1">
              {section.items.map((item) => {
                const Icon = item.icon
                const active = isParentActive(item)
                const hasChildren = Boolean(item.children && item.children.length > 0)
                const isExpanded = isParentExpanded(item)

                return (
                  <div key={item.label}>
                    <button
                      type="button"
                      aria-current={active ? 'page' : undefined}
                      onClick={() => {
                        handleNavClick(item)
                        if (hasChildren) {
                          toggleParent(item.label)
                        }
                      }}
                      className={`flex w-full items-center gap-3 rounded-r-lg rounded-l-none pl-3 pr-3 py-2.5 text-left text-[14px] font-semibold transition border-l-4 ${
                        active
                          ? 'bg-white/15 text-white border-l-[#b4ecf2] border-y-0 border-r-0'
                          : 'border-l-transparent border-y-0 border-r-0 text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-white/70'}`} />
                      <span className="flex-1 whitespace-normal leading-snug break-words">{item.label}</span>
                      {hasChildren && (
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                    </button>

                    {/* Child Menu Items */}
                    {hasChildren && isExpanded && (
                      <div className="space-y-1 pl-3 mt-1">
                        {item.children?.map((child) => {
                          const ChildIcon = child.icon || Icon
                          const childActive = isChildActive(item, child)

                          return (
                            <button
                              key={child.label}
                              type="button"
                              aria-current={childActive ? 'page' : undefined}
                              onClick={() => {
                                navigate(child.path)
                                onClose()
                              }}
                              className={`flex w-full items-center gap-2.5 rounded-r-lg rounded-l-none pl-6 pr-3 py-2 text-left text-[14px] font-medium transition ${
                                childActive
                                  ? 'bg-white/10 text-white font-semibold'
                                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <ChildIcon className={`h-[16px] w-[16px] shrink-0 ${childActive ? 'text-white' : 'text-white/70'}`} />
                              <span className="flex-1 whitespace-normal leading-snug break-words">{child.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  )
}

export default PermissionAwareSidebar
